// src/components/Organization/EmailTemplateManager.tsx

// What? Email template management for organization admins
// Why? To let admins customise email subjects and bodies for notifications
// How? List view + edit view with subject/body/variables panel

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  Table,
  Sheet,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/joy";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import { useAuthFetch } from "../../utils/useAuthFetch";
import { useOrgEmailStatus } from "../../hooks/useOrgEmailStatus";
import EmailTemplateEditor from "./EmailTemplateEditor";
import DisabledEmailOverlay from "./DisabledEmailOverlay";

interface EmailTemplateManagerProps {
  organizationId: string;
}

interface TemplateInfo {
  template_key: string;
  module: string;
  display_name: string;
  subject_template: string;
  is_active: boolean;
  is_system: boolean;
  available_variables: { name: string; description: string }[];
}

interface TemplateDetail extends TemplateInfo {
  body_template: string;
}

const MODULE_COLORS: Record<
  string,
  "primary" | "success" | "warning" | "neutral"
> = {
  myvault: "primary",
  core: "neutral",
  ida: "success",
  bkg: "warning",
};

const EmailTemplateManager: React.FC<EmailTemplateManagerProps> = ({
  organizationId,
}) => {
  const authFetch = useAuthFetch();
  const { enabled: emailEnabled, loading: emailStatusLoading } =
    useOrgEmailStatus(organizationId);
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [editing, setEditing] = useState<TemplateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authFetch(
        `/api/email/organization/${organizationId}/templates`,
        "fetch email templates",
      );
      if (Array.isArray(data)) {
        setTemplates(data);
      }
    } catch {
      // Templates not available
    } finally {
      setLoading(false);
    }
  }, [authFetch, organizationId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleEdit = async (templateKey: string) => {
    try {
      const data = await authFetch(
        `/api/email/organization/${organizationId}/templates/${templateKey}`,
        "load template detail",
      );
      setEditing(data as TemplateDetail);
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to load template: ${err}`,
      });
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    setFeedback(null);
    try {
      await authFetch(
        `/api/email/organization/${organizationId}/templates/${editing.template_key}`,
        "save template",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subject_template: editing.subject_template,
            body_template: editing.body_template,
            is_active: editing.is_active,
          }),
        },
      );
      setFeedback({ type: "success", message: "Template saved" });
      await loadTemplates();
    } catch (err) {
      setFeedback({ type: "danger", message: `Failed to save: ${err}` });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleReset = async () => {
    if (!editing) return;
    setSaving(true);
    setFeedback(null);
    try {
      await authFetch(
        `/api/email/organization/${organizationId}/templates/${editing.template_key}`,
        "reset template",
        { method: "DELETE" },
      );
      setFeedback({ type: "success", message: "Template reset to default" });
      setEditing(null);
      await loadTemplates();
    } catch (err) {
      setFeedback({ type: "danger", message: `Failed to reset: ${err}` });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  // Variable insertion is now handled by EmailTemplateEditor

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size="sm" />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Edit view
  if (editing) {
    const editBody = (
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="sm"
            variant="plain"
            onClick={() => {
              setEditing(null);
              setFeedback(null);
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <EmailIcon sx={{ color: "primary.500" }} />
          <Typography level="title-lg">{editing.display_name}</Typography>
          <Chip size="sm" color={MODULE_COLORS[editing.module] || "neutral"}>
            {editing.module}
          </Chip>
          {!editing.is_system && (
            <Chip size="sm" variant="soft" color="warning">
              customised
            </Chip>
          )}
        </Stack>

        {/* Template editor with MMEditor, variable insertion, and preview */}
        <EmailTemplateEditor
          subjectTemplate={editing.subject_template}
          onSubjectChange={(val) =>
            setEditing((prev) =>
              prev ? { ...prev, subject_template: val } : prev,
            )
          }
          bodyTemplate={editing.body_template}
          onBodyChange={(val) =>
            setEditing((prev) =>
              prev ? { ...prev, body_template: val } : prev,
            )
          }
          variables={editing.available_variables}
          organizationId={organizationId}
          templateKey={editing.template_key}
          emailEnabled={emailEnabled}
        />

        {/* Feedback */}
        {feedback && (
          <Alert color={feedback.type} size="sm">
            {feedback.message}
          </Alert>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={1.5}>
          <Button
            size="sm"
            startDecorator={<SaveIcon />}
            loading={saving}
            onClick={handleSave}
          >
            Save
          </Button>
          {!editing.is_system && (
            <Button
              size="sm"
              variant="outlined"
              color="warning"
              startDecorator={<RestoreIcon />}
              onClick={handleReset}
            >
              Reset to Default
            </Button>
          )}
        </Stack>
      </Stack>
    );

    return (
      <Card variant="outlined">
        <CardContent>
          {!emailStatusLoading && !emailEnabled ? (
            <DisabledEmailOverlay>{editBody}</DisabledEmailOverlay>
          ) : (
            editBody
          )}
        </CardContent>
      </Card>
    );
  }

  // List view
  const listBody = (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <EmailIcon sx={{ color: "primary.500" }} />
        <Typography level="title-lg">Email Templates</Typography>
        <Typography level="body-xs" sx={{ color: "text.tertiary", ml: "auto" }}>
          {templates.length} templates
        </Typography>
      </Box>

      <Divider />

      {templates.length === 0 ? (
        <Typography
          level="body-sm"
          sx={{ color: "text.tertiary", textAlign: "center", py: 3 }}
        >
          No email templates available. Configure email integration first.
        </Typography>
      ) : (
        <Sheet
          variant="outlined"
          sx={{ borderRadius: "sm", overflow: "hidden" }}
        >
          <Table size="sm" stickyHeader>
            <thead>
              <tr>
                <th style={{ width: "35%" }}>Template</th>
                <th style={{ width: "20%" }}>Module</th>
                <th style={{ width: "25%" }}>Subject</th>
                <th style={{ width: "10%" }}>Status</th>
                <th style={{ width: "10%" }} />
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.template_key}>
                  <td>
                    <Typography level="body-sm">{t.display_name}</Typography>
                    <Typography
                      level="body-xs"
                      sx={{
                        color: "text.tertiary",
                        fontFamily: "monospace",
                      }}
                    >
                      {t.template_key}
                    </Typography>
                  </td>
                  <td>
                    <Chip
                      size="sm"
                      color={MODULE_COLORS[t.module] || "neutral"}
                      variant="soft"
                    >
                      {t.module}
                    </Chip>
                  </td>
                  <td>
                    <Typography level="body-xs" noWrap>
                      {t.subject_template}
                    </Typography>
                  </td>
                  <td>
                    {t.is_system ? (
                      <Chip size="sm" variant="soft" color="neutral">
                        default
                      </Chip>
                    ) : (
                      <Chip size="sm" variant="soft" color="warning">
                        customised
                      </Chip>
                    )}
                  </td>
                  <td>
                    <IconButton
                      size="sm"
                      variant="plain"
                      onClick={() => handleEdit(t.template_key)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      {feedback && (
        <Alert color={feedback.type} size="sm">
          {feedback.message}
        </Alert>
      )}
    </Stack>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        {!emailStatusLoading && !emailEnabled ? (
          <DisabledEmailOverlay>{listBody}</DisabledEmailOverlay>
        ) : (
          listBody
        )}
      </CardContent>
    </Card>
  );
};

export default EmailTemplateManager;
